import { useEffect, useState } from "react";
import api, { errMsg, fmtMoney, fmtDate, downloadReport } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { StatusBadge, Empty } from "./common";
import { toast } from "sonner";
import { Search, FileDown, History, RotateCcw } from "lucide-react";

/** Admin/approver audit view of deleted requisitions and their justification; admins can restore. */
export function DeletedRequisitionsDialog({ open, onOpenChange, onRestored }) {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [dq, setDq] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [sel, setSel] = useState(null);
  const [loading, setLoading] = useState(false);
  const [restoring, setRestoring] = useState("");
  const [tick, setTick] = useState(0);

  useEffect(() => { const h = setTimeout(() => setDq(q.trim()), 300); return () => clearTimeout(h); }, [q]);
  useEffect(() => {
    if (!open) return;
    setLoading(true);
    const params = {};
    if (dq) params.q = dq;
    if (from) params.date_from = from;
    if (to) params.date_to = to;
    api.get("/requisitions/deleted", { params }).then((r) => setRows(r.data)).catch((e) => toast.error(errMsg(e))).finally(() => setLoading(false));
  }, [open, dq, from, to, tick]);

  const restore = async (r) => {
    if (!window.confirm(`¿Recuperar la requisición ${r.number}? Volverá a aparecer con el estatus que tenía (${r.status}).`)) return;
    setRestoring(r.id);
    try {
      await api.post(`/requisitions/deleted/${r.id}/restore`);
      toast.success(`Requisición ${r.number} recuperada`);
      setSel(null); setTick((x) => x + 1); onRestored?.();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setRestoring(""); }
  };

  const exportXlsx = async () => {
    try { await downloadReport("/requisitions/deleted/export", "requisiciones_eliminadas.xlsx"); }
    catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none sm:max-w-6xl max-h-[90vh] overflow-y-auto" data-testid="deleted-reqs-dialog">
        <DialogHeader>
          <DialogTitle className="uppercase font-black flex items-center gap-2"><History size={16} /> Historial de requisiciones eliminadas</DialogTitle>
          <DialogDescription className="text-xs">Copia de auditoría: quién eliminó cada requisición, cuándo y con qué justificación.</DialogDescription>
        </DialogHeader>
        <div className="flex flex-wrap gap-2 items-end">
          <div className="relative flex-1 min-w-56">
            <Search size={11} className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Folio, solicitante, quien eliminó, justificación, artículo…" className="rounded-none h-8 text-xs pl-6" data-testid="deleted-reqs-search" />
          </div>
          <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="rounded-none h-8 text-xs w-36" data-testid="deleted-reqs-from" />
          <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="rounded-none h-8 text-xs w-36" data-testid="deleted-reqs-to" />
          <Button size="sm" variant="outline" className="rounded-none text-xs font-bold" onClick={exportXlsx} data-testid="deleted-reqs-export"><FileDown size={12} className="mr-1" /> Excel</Button>
        </div>
        {loading ? <div className="text-xs text-muted-foreground p-4">Cargando…</div> : rows.length === 0 ? <Empty text="Sin requisiciones eliminadas" /> : (
          <div className="tbl-wrap">
            <table className="tbl" data-testid="deleted-reqs-table">
              <thead><tr><th>Folio</th><th>Estatus al borrar</th><th>Solicitante</th><th>Depto</th><th className="num">Partidas</th><th className="num">Total est.</th><th>Eliminada por</th><th>Fecha</th><th>Justificación</th>{user?.role === "admin" && <th></th>}</tr></thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} className="cursor-pointer" onClick={() => setSel(sel?.id === r.id ? null : r)} data-testid={`deleted-req-row-${r.number}`}>
                    <td className="font-bold">{r.number}</td>
                    <td><StatusBadge status={r.status} /></td>
                    <td className="text-[11px]">{r.requester_email}</td>
                    <td className="text-[11px]">{r.department || "—"}</td>
                    <td className="num">{r.lines?.length || 0}</td>
                    <td className="num">{fmtMoney(r.total)}</td>
                    <td className="text-[11px]">{r.deleted_by}</td>
                    <td className="text-[11px] text-muted-foreground">{fmtDate(r.deleted_at)}</td>
                    <td className="text-[11px] max-w-64 truncate" title={r.delete_reason} data-testid={`deleted-req-reason-${r.number}`}>{r.delete_reason}</td>
                    {user?.role === "admin" && (
                      <td onClick={(e) => e.stopPropagation()}>
                        <Button size="sm" variant="outline" className="rounded-none text-[11px] h-7 font-bold" disabled={restoring === r.id}
                          onClick={() => restore(r)} data-testid={`deleted-req-restore-${r.number}`}>
                          <RotateCcw size={11} className="mr-1" /> {restoring === r.id ? "Recuperando…" : "Recuperar"}
                        </Button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {sel && (
          <div className="border p-3 text-xs space-y-2" data-testid="deleted-req-detail">
            <div className="font-black uppercase">{sel.number} · detalle al momento de eliminar</div>
            <div className="grid grid-cols-2 gap-x-6 gap-y-1">
              <div><b>Justificación:</b> {sel.delete_reason}</div>
              <div><b>Eliminada por:</b> {sel.deleted_by} · {fmtDate(sel.deleted_at)}</div>
              <div><b>Solicitante:</b> {sel.requester_email}</div>
              <div><b>Aprobador:</b> {sel.approver_email || sel.assigned_approver_email || "—"}</div>
              <div><b>Almacén:</b> {sel.warehouse || "—"}</div>
              <div><b>Creada:</b> {fmtDate(sel.created_at)}</div>
              {sel.notes && <div className="col-span-2"><b>Notas:</b> {sel.notes}</div>}
            </div>
            <table className="tbl">
              <thead><tr><th>Artículo</th><th>No. parte</th><th>Descripción</th><th className="num">Cant.</th><th className="num">Costo est.</th></tr></thead>
              <tbody>{(sel.lines || []).map((l, i) => (
                <tr key={i}><td className="font-bold">{l.item_number}</td><td className="font-mono text-[11px]">{l.part_number || "—"}</td><td>{l.description}</td><td className="num">{l.qty}</td><td className="num">{fmtMoney(l.est_cost)}</td></tr>
              ))}</tbody>
            </table>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
